import React, { useEffect, useState } from "react";
import { Row, Table, Typography, message } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getResultSummaryByKey,
  getTestResultLog,
} from "../../service/result.service";
import RunStatusBadge from "../../components/dashboard/RunStatusBadge";
import PageHeader from "../../components/generic/PageHeader";
import "../../styles/db.css";

const NAVY = "#0B1F3A";
const MUTED = "#6B7B8C";
const INK = "#2D3748";

const mono = (v) => (
  <code
    style={{
      fontSize: 11,
      background: "#f5f7fa",
      padding: "2px 6px",
      borderRadius: 4,
      wordBreak: "break-all",
    }}
  >
    {v || "—"}
  </code>
);

/**
 * Operation-style result detail for a process-automation step (file move/copy/
 * delete, polling, SQL event, extract). NOT a pass/fail test breakdown — shows the
 * operation, the files affected, and their source/destination paths.
 */
function ProcessAutomationResultDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const key = location.state?.key;
  const [summary, setSummary] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!key) {
      navigate("/results");
      return;
    }
    setLoading(true);
    Promise.all([getResultSummaryByKey(key), getTestResultLog(key)])
      .then(([s, f]) => {
        setSummary(s.data || null);
        setFiles(f.data || []);
      })
      .catch(() => message.error("Failed to load process automation result"))
      .finally(() => setLoading(false));
  }, [key, navigate]);

  const operation = summary?.subCategory || "Process Automation";
  const status = summary?.testResult || summary?.jobStatus || "—";

  const meta = [
    { label: "Operation", value: operation },
    { label: "Source", value: summary?.inputName || "—", mono: true },
    { label: "Run by", value: summary?.user || "—" },
    { label: "Started", value: summary?.runStart || "—" },
    { label: "Ended", value: summary?.runEnd || "—" },
    { label: "Files affected", value: String(files.length) },
  ];

  const columns = [
    {
      title: "File name",
      dataIndex: "actualValue",
      key: "actualValue",
      render: (v) => (
        <span style={{ fontSize: 12, fontWeight: 500, color: INK }}>
          {v || "—"}
        </span>
      ),
    },
    {
      title: "Source path",
      dataIndex: "columnName",
      key: "columnName",
      render: (v) => <Typography.Text copyable={!!v}>{mono(v)}</Typography.Text>,
    },
    {
      title: "Destination / outcome",
      dataIndex: "expectedValue",
      key: "expectedValue",
      render: (v) => (
        <Typography.Text copyable={!!v && v !== "(deleted from source)"}>
          {mono(v)}
        </Typography.Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "testResult",
      key: "testResult",
      width: 120,
      align: "center",
      render: (v) => <RunStatusBadge status={v} size="sm" />,
    },
  ];

  return (
    <div className="db-container">
      <PageHeader
        backTo={-1}
        icon={<ThunderboltOutlined />}
        title={operation}
        subtitle="Process automation · operation result"
        actions={<RunStatusBadge status={status} />}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 12,
          padding: "0 4px 16px",
        }}
      >
        {meta.map((m) => (
          <div
            key={m.label}
            style={{
              background: "var(--color-background-secondary, #f5f7fa)",
              border: "1px solid var(--color-border-tertiary, #e4e9f0)",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>
              {m.label}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: NAVY,
                wordBreak: "break-all",
                fontFamily: m.mono ? "monospace" : undefined,
              }}
            >
              {m.value}
            </div>
          </div>
        ))}
      </div>

      <Row className="table-container">
        <Table
          columns={columns}
          dataSource={files}
          rowKey={(r, i) => r.key || i}
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: "No file details recorded for this operation" }}
        />
      </Row>
    </div>
  );
}

export default ProcessAutomationResultDetail;
